import React, { useEffect } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const TermsConditions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 text-gray-600 hover:text-orange-600"
          >
            <ArrowLeft size={20} />
            Voltar ao Início
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center">
              <FileText className="text-orange-600" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Termos e Condições</h1>
              <p className="text-gray-600 mt-1">Última atualização: {new Date().toLocaleDateString('pt-PT')}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none space-y-8">
            {/* Introdução */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introdução</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bem-vindo ao website da <strong>Somar Distâncias, Lda.</strong> (NIF: 510210848). Estes Termos e Condições 
                regem a utilização deste website e dos serviços nele disponibilizados.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Ao aceder e utilizar este website, aceita estar vinculado a estes Termos e Condições. Se não concordar 
                com alguma parte destes termos, não deve utilizar o nosso website.
              </p>
            </section>

            {/* Utilização do Website */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Utilização do Website</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1. Uso Permitido</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este website destina-se a fornecer informações sobre os serviços de construção civil oferecidos pela 
                Somar Distâncias, Lda. Pode utilizar o website para:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Obter informações sobre os nossos serviços</li>
                <li>Solicitar orçamentos</li>
                <li>Entrar em contacto connosco</li>
                <li>Ver exemplos dos nossos projetos</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2. Uso Proibido</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Não pode utilizar este website para:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Qualquer finalidade ilegal ou não autorizada</li>
                <li>Transmitir vírus, malware ou qualquer código malicioso</li>
                <li>Tentar obter acesso não autorizado a qualquer parte do website</li>
                <li>Copiar, reproduzir ou distribuir conteúdo sem autorização</li>
                <li>Fazer engenharia reversa ou tentar extrair o código-fonte</li>
                <li>Utilizar o website de forma que possa danificá-lo ou prejudicar o seu desempenho</li>
              </ul>
            </section>

            {/* Propriedade Intelectual */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Propriedade Intelectual</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1. Conteúdo do Website</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Todo o conteúdo presente neste website, incluindo mas não limitado a textos, gráficos, logotipos, 
                ícones, imagens, vídeos e software, é propriedade da Somar Distâncias, Lda. ou dos seus fornecedores 
                de conteúdo e está protegido pelas leis portuguesas e internacionais de propriedade intelectual.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2. Marcas Registadas</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                O nome "Somar Distâncias" e o logotipo são marcas da Somar Distâncias, Lda. Não podem ser utilizados 
                sem a nossa autorização prévia por escrito.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3. Licença Limitada</h3>
              <p className="text-gray-700 leading-relaxed">
                Concedemos-lhe uma licença limitada, não exclusiva e não transferível para aceder e utilizar o website 
                para fins pessoais e não comerciais. Esta licença não inclui o direito de descarregar (exceto cache de página) 
                ou modificar qualquer parte do website.
              </p>
            </section>

            {/* Orçamentos e Serviços */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Orçamentos e Serviços</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1. Pedidos de Orçamento</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Os pedidos de orçamento efetuados através do website são gratuitos e não vinculativos. Os orçamentos 
                apresentados são válidos pelo período indicado no documento e podem estar sujeitos a alterações.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2. Informações Fornecidas</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                As informações sobre serviços disponibilizadas neste website são meramente indicativas. Para informações 
                detalhadas e personalizadas, entre em contacto direto connosco.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3. Contratos de Prestação de Serviços</h3>
              <p className="text-gray-700 leading-relaxed">
                Qualquer contrato de prestação de serviços de construção será regido por termos e condições específicos 
                acordados entre as partes e formalizados por escrito.
              </p>
            </section>

            {/* Proteção de Dados */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Proteção de Dados Pessoais</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                O tratamento dos seus dados pessoais é efetuado em conformidade com o RGPD e a legislação portuguesa 
                aplicável. Para mais informações, consulte a nossa{' '}
                <a href="/politica-privacidade" className="text-orange-600 hover:underline font-medium">
                  Política de Privacidade
                </a>.
              </p>
            </section>

            {/* Links Externos */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Links para Sites Externos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este website pode conter links para sites de terceiros. Estes links são fornecidos apenas para sua 
                conveniência. Não temos controlo sobre o conteúdo desses sites e não somos responsáveis pelo seu conteúdo 
                ou práticas de privacidade.
              </p>
              <p className="text-gray-700 leading-relaxed">
                A inclusão de um link não implica o nosso endosso do site vinculado. O uso de qualquer site vinculado 
                é por sua conta e risco.
              </p>
            </section>

            {/* Limitação de Responsabilidade */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitação de Responsabilidade</h2>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">7.1. Disponibilidade do Website</h3>
                <p className="text-gray-700 leading-relaxed">
                  Embora nos esforcemos para manter o website disponível 24/7, não garantimos que o website estará 
                  sempre disponível ou livre de erros. Podemos suspender, retirar ou restringir a disponibilidade de 
                  todo ou parte do website por razões comerciais e operacionais.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.2. Precisão da Informação</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Embora nos esforcemos para garantir que as informações no website sejam precisas e atualizadas, 
                não fazemos declarações ou garantias de qualquer tipo, expressas ou implícitas, sobre a completude, 
                precisão, fiabilidade ou disponibilidade das informações.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">7.3. Exclusão de Garantias</h3>
              <p className="text-gray-700 leading-relaxed">
                Na medida máxima permitida pela lei aplicável, excluímos todas as garantias, condições e outros termos 
                que possam estar implícitos por lei ou common law.
              </p>
            </section>

            {/* Indenização */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Indenização</h2>
              <p className="text-gray-700 leading-relaxed">
                Concorda em indemnizar, defender e isentar a Somar Distâncias, Lda., os seus diretores, funcionários, 
                empregados e agentes de e contra todas as reclamações, responsabilidades, danos, perdas e despesas, 
                incluindo honorários razoáveis de advogados, decorrentes ou relacionados com:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-3">
                <li>A sua utilização do website</li>
                <li>Violação destes Termos e Condições</li>
                <li>Violação de direitos de terceiros</li>
              </ul>
            </section>

            {/* Alterações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Alterações aos Termos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Reservamo-nos o direito de modificar estes Termos e Condições a qualquer momento. As alterações entrarão 
                em vigor imediatamente após a sua publicação no website.
              </p>
              <p className="text-gray-700 leading-relaxed">
                A sua continuação da utilização do website após a publicação de alterações constituirá a sua aceitação 
                dessas alterações. Recomendamos que reveja periodicamente estes termos.
              </p>
            </section>

            {/* Lei Aplicável */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Lei Aplicável e Jurisdição</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Estes Termos e Condições são regidos e interpretados de acordo com as leis de Portugal. Qualquer disputa 
                decorrente ou relacionada com estes termos estará sujeita à jurisdição exclusiva dos tribunais portugueses.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Em caso de conflito entre a versão portuguesa e qualquer tradução destes termos, a versão portuguesa prevalecerá.
              </p>
            </section>

            {/* Divisibilidade */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Divisibilidade</h2>
              <p className="text-gray-700 leading-relaxed">
                Se qualquer disposição destes Termos e Condições for considerada inválida ou inexequível, essa disposição 
                será eliminada ou limitada na medida mínima necessária, e as restantes disposições destes termos continuarão 
                em pleno vigor e efeito.
              </p>
            </section>

            {/* Acordo Completo */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Acordo Completo</h2>
              <p className="text-gray-700 leading-relaxed">
                Estes Termos e Condições, juntamente com a nossa Política de Privacidade e Política de Cookies, 
                constituem o acordo completo entre si e a Somar Distâncias, Lda. relativamente à utilização do website.
              </p>
            </section>

            {/* Contacto */}
            <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contacto</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para questões sobre estes Termos e Condições, entre em contacto connosco:
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-gray-700">
                  <strong>Somar Distâncias, Lda.</strong>
                </p>
                <p className="text-gray-700">
                  Estrada de Santa Eulália, Horta Preta, Casal das Oliveiras<br />
                  2670-351 Loures, Portugal
                </p>
                <p className="text-gray-700">
                  <strong>NIF:</strong> 510210848
                </p>
                <p className="text-gray-700">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline">
                    somardistancias8.geral@gmail.com
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>Telefone:</strong>{' '}
                  <a href="tel:+351961313663" className="text-orange-600 hover:underline">
                    +351 961 313 663
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Back Button */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <Button
              onClick={() => navigate('/')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
